import React from 'react';
import { motion } from 'framer-motion';
import { Zap, Users, Globe2, Trophy } from 'lucide-react';
import CTAPair from '@/components/site/CTAPair';

const pillars = [
  {
    icon: Zap,
    title: 'Faster and leaner than an agency.',
    body: 'No account-management layers, no three-week turnarounds, no markup on top of markup. You talk to the people actually running your media.',
  },
  {
    icon: Users,
    title: 'No headcount required.',
    body: 'The output of a full in-house media team without hiring, onboarding, or managing one. We’re the growth function you can turn on this week.',
  },
  {
    icon: Globe2,
    title: 'Beyond the walled gardens.',
    body: 'Meta and Google are saturated and expensive. Our first-party data finds net-new buyers across the open web — audiences competitors aren’t already paying triple for.',
  },
  {
    icon: Trophy,
    title: 'Results, plainly reported.',
    body: 'At the end of the day, what you need is results — measured against the number you set, reported in plain English.',
  },
];

export default function WhySellrly({ id = 'why-sellrly', titleCase = false }) {
  return (
    <section id={id} className="relative py-24 md:py-32 bg-white text-[#031D44]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-4xl">
          <p className="text-sm sm:text-base font-semibold uppercase tracking-[0.22em] text-[#FF5081]">Why Sellrly</p>
          <h2 className={`mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-semibold tracking-[-0.025em] leading-[1.02] text-[#031D44] ${titleCase ? 'capitalize' : ''}`}>
            The growth function you can turn on this week.
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          {pillars.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-2xl p-8 md:p-9 border border-[#031D44]/10 bg-[#F8F7FC]"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#031D44] text-white">
                <p.icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
              </span>
              <h3 className="mt-5 font-display text-xl md:text-[22px] font-semibold tracking-tight">{p.title}</h3>
              <p className="mt-3 text-[#031D44]/60 leading-relaxed text-[15px]">{p.body}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <p className="lg:col-span-7 text-lg lg:text-xl leading-relaxed text-[#031D44]/70 max-w-2xl">
            Built so a small operating team can move quickly — without the bureaucracy or the headcount.
          </p>
          <div className="lg:col-span-5 lg:justify-self-end">
            <CTAPair testIdSuffix="why" secondaryTarget="/contact#contact-form" tone="light" />
          </div>
        </div>
      </div>
    </section>
  );
}
